import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type {
  RegistroVigilancia,
  AreaVigilancia,
  TipoExamen,
  ResultadoExamen,
} from './models/vigilancia-salud.model';

const STORAGE_KEY = 'vigilancia_registros';

const AREAS: AreaVigilancia[] = [
  { id: '1', nombre: 'Producción' },
  { id: '2', nombre: 'Mantenimiento' },
  { id: '3', nombre: 'Almacén' },
  { id: '4', nombre: 'Oficinas' },
  { id: '5', nombre: 'Laboratorio' },
];

function demoRegistros(): RegistroVigilancia[] {
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const next = (daysFromNow: number) =>
    new Date(Date.now() + daysFromNow * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  return [
    {
      id: '1',
      trabajador: 'Juan Pérez',
      areaId: '1',
      puesto: 'Operario de máquina',
      tipoExamen: 'periodico',
      fechaRealizacion: d(30),
      fechaProximoControl: next(335),
      resultado: 'apto',
      centroMedico: 'Clínica del Trabajo',
      medico: 'Dra. María López',
      observaciones: 'Examen anual sin hallazgos.',
      restricciones: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      trabajador: 'Ana Martínez',
      areaId: '5',
      puesto: 'Analista de calidad',
      tipoExamen: 'ingreso',
      fechaRealizacion: d(5),
      fechaProximoControl: next(360),
      resultado: 'apto_restricciones',
      centroMedico: 'Centro Médico Ocupacional',
      medico: 'Dr. Carlos Ruiz',
      observaciones: 'Apto para laboratorio con uso de EPP en zona de reactivos.',
      restricciones: 'Evitar manipulación directa de sustancias sin EPP completo.',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '3',
      trabajador: 'Luis Fernández',
      areaId: '2',
      puesto: 'Técnico de mantenimiento',
      tipoExamen: 'retorno',
      fechaRealizacion: d(15),
      fechaProximoControl: next(350),
      resultado: 'apto',
      centroMedico: 'Clínica del Trabajo',
      medico: 'Dra. María López',
      observaciones: 'Reincorporación tras baja por lesión. Sin limitaciones.',
      restricciones: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '4',
      trabajador: 'Carmen Soto',
      areaId: '1',
      puesto: 'Supervisora de línea',
      tipoExamen: 'periodico',
      fechaRealizacion: d(400),
      fechaProximoControl: d(35),
      resultado: 'apto',
      centroMedico: 'Clínica del Trabajo',
      medico: 'Dr. Carlos Ruiz',
      observaciones: 'Próximo control programado.',
      restricciones: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '5',
      trabajador: 'Pedro Sánchez',
      areaId: '3',
      puesto: 'Almacenista',
      tipoExamen: 'cambio_puesto',
      fechaRealizacion: d(60),
      fechaProximoControl: next(305),
      resultado: 'apto',
      centroMedico: 'Centro Médico Ocupacional',
      medico: 'Dra. María López',
      observaciones: 'Cambio desde oficinas a almacén. Evaluación de carga física.',
      restricciones: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '6',
      trabajador: 'Rosa Díaz',
      areaId: '4',
      puesto: 'Auxiliar administrativo',
      tipoExamen: 'periodico',
      fechaRealizacion: d(90),
      fechaProximoControl: next(275),
      resultado: 'pendiente',
      centroMedico: '',
      medico: '',
      observaciones: 'Programado para próximo mes.',
      restricciones: '',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class VigilanciaSaludService {
  private items: RegistroVigilancia[] = demoRegistros();

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const data = await this.demoStorage.get<RegistroVigilancia[]>(STORAGE_KEY);
    this.items = Array.isArray(data) ? data : demoRegistros();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return this.demoStorage.set(STORAGE_KEY, this.items).catch(() => {});
  }

  getAreas(): AreaVigilancia[] {
    return [...AREAS];
  }

  getList(filters?: {
    busqueda?: string;
    areaId?: string;
    tipoExamen?: TipoExamen;
    resultado?: ResultadoExamen;
  }): RegistroVigilancia[] {
    let list = [...this.items];
    if (filters?.busqueda?.trim()) {
      const q = filters.busqueda.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.trabajador.toLowerCase().includes(q) ||
          r.puesto.toLowerCase().includes(q) ||
          r.centroMedico.toLowerCase().includes(q) ||
          r.medico.toLowerCase().includes(q)
      );
    }
    if (filters?.areaId) list = list.filter((r) => r.areaId === filters.areaId);
    if (filters?.tipoExamen) list = list.filter((r) => r.tipoExamen === filters.tipoExamen);
    if (filters?.resultado) list = list.filter((r) => r.resultado === filters.resultado);
    return list.sort((a, b) => b.fechaRealizacion.localeCompare(a.fechaRealizacion));
  }

  getById(id: string): RegistroVigilancia | null {
    return this.items.find((r) => r.id === id) ?? null;
  }

  create(dto: Omit<RegistroVigilancia, 'id' | 'createdAt' | 'updatedAt'>): RegistroVigilancia {
    const id = String(Date.now());
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: RegistroVigilancia = {
      ...dto,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.items.push(nuevo);
    this.persistDemo();
    return nuevo;
  }

  update(id: string, dto: Partial<Omit<RegistroVigilancia, 'id' | 'createdAt'>>): RegistroVigilancia | null {
    const idx = this.items.findIndex((r) => r.id === id);
    if (idx === -1) return null;
    const updated: RegistroVigilancia = {
      ...this.items[idx],
      ...dto,
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
    };
    this.items[idx] = updated;
    this.persistDemo();
    return updated;
  }

  delete(id: string): boolean {
    const idx = this.items.findIndex((r) => r.id === id);
    if (idx === -1) return false;
    this.items.splice(idx, 1);
    this.persistDemo();
    return true;
  }
}
